import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { createShop, bulkCreateServices } from "../lib/api";
import TawkChat from "../components/TawkChat";

const DEFAULT_SERVICES = [
  { name: "Consultation", duration_minutes: 15, price: 0, sort_order: 1 },
  { name: "Standard Service", duration_minutes: 30, price: 500, sort_order: 2 },
  { name: "Premium Service", duration_minutes: 60, price: 1500, sort_order: 3 },
];

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 40);
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [slug, setSlug] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const finalSlug = slug.trim() || slugify(name);
  const canSubmit =
    name.trim().length >= 3 &&
    finalSlug.length >= 3 &&
    /^\d{4}$/.test(pin) &&
    pin === confirmPin &&
    !submitting;

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const shop = await createShop({
        slug: finalSlug,
        name: name.trim(),
        tagline: tagline.trim() || "Smart Queue Experience",
        admin_pin: pin,
      });
      await bulkCreateServices(shop.id, DEFAULT_SERVICES);
      toast.success("Shop created! Welcome to AIBox.");
      navigate(`/s/${finalSlug}/admin`);
    } catch (err) {
      toast.error(err.message || "Could not create shop");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-void text-white relative overflow-hidden">
      {/* Tawk.to live chat — Sales page (signup) */}
      <TawkChat />
      <div className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold/8 blur-[120px]" />

      <header className="relative z-10 px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="signup-home">
          <div className="w-9 h-9 rounded-full bg-void border border-gold/40 flex items-center justify-center gold-glow">
            <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base">AIBox Global</div>
            <div className="font-numbers text-[10px] tracking-[0.25em] uppercase text-white/40">
              Queue Platform
            </div>
          </div>
        </Link>
        <Link to="/" className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </header>

      <Toaster theme="dark" position="top-center" richColors />

      <main className="relative z-10 px-5 md:px-10 pb-16 max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold">
            Start Your Shop
          </div>
          <h1 className="font-heading text-4xl md:text-5xl mt-3 tracking-tight">
            Launch in <em className="not-italic italic text-gold">60 seconds</em>.
          </h1>
          <p className="text-white/60 mt-4 text-base">
            White-label queue management for your business. Free during beta.
          </p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-8 glass rounded-2xl p-6 md:p-8 space-y-6"
          data-testid="signup-form"
        >
          <Field label="Shop Name">
            <input
              className="input-minimal"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slug) setSlug(slugify(e.target.value));
              }}
              placeholder="The Luxury Lounge"
              data-testid="signup-name"
            />
          </Field>

          <Field label="Tagline (optional)">
            <input
              className="input-minimal"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="Premium grooming since 1998"
              data-testid="signup-tagline"
            />
          </Field>

          <Field label="Booking URL Slug (auto-generated)">
            <div className="flex items-baseline gap-1">
              <span className="font-numbers text-[10px] tracking-[0.2em] text-white/40">
                /s/
              </span>
              <input
                className="input-minimal flex-1"
                value={slug}
                onChange={(e) => setSlug(slugify(e.target.value))}
                placeholder="luxury-lounge"
                data-testid="signup-slug"
              />
              <span className="font-numbers text-[10px] tracking-[0.2em] text-white/40">
                /book
              </span>
            </div>
            <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40 mt-2">
              Auto-filled from shop name · edit to customize · subdomain mapping optional, configure later in admin
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Admin PIN (4 digits)">
              <input
                type="password"
                className="input-minimal font-numbers tracking-[0.4em]"
                value={pin}
                inputMode="numeric"
                maxLength={4}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="••••"
                data-testid="signup-pin"
              />
            </Field>
            <Field label="Confirm PIN">
              <input
                type="password"
                className="input-minimal font-numbers tracking-[0.4em]"
                value={confirmPin}
                inputMode="numeric"
                maxLength={4}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="••••"
                data-testid="signup-pin-confirm"
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="signup-submit"
            className="btn-gold w-full inline-flex items-center justify-center gap-2"
          >
            {submitting ? "Creating…" : "Create My Shop"}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>

          <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/30 text-center">
            3 starter services auto-created · Edit anytime
          </div>
        </motion.form>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
        {label}
      </label>
      {children}
    </div>
  );
}
